import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Package, Clock, CheckCircle, Truck, XCircle, Send,
  ChevronDown, ChevronUp, Search, Phone, MapPin, Calendar,
  RefreshCcw, ArrowRight, DollarSign, MessageCircle, FileText,
  IndianRupee, ExternalLink, Copy, Check, Image
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const statusConfig = {
  pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', label: 'Pending Review' },
  quotation_sent: { icon: Send, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Quotation Sent' },
  confirmed: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Confirmed' },
  processing: { icon: Truck, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', label: 'Processing' },
  completed: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Completed' },
  cancelled: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Cancelled' }
};

const STATUS_TRANSITIONS = {
  pending: ['quotation_sent', 'cancelled'],
  quotation_sent: ['confirmed', 'cancelled'],
  confirmed: ['processing', 'cancelled'],
  processing: ['completed', 'cancelled'],
  completed: [],
  cancelled: []
};

const AdminRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState(null);
  const [showQuoteDialog, setShowQuoteDialog] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    purchase_cost: '',
    service_fee: '',
    delivery_fee: '',
    admin_notes: ''
  });
  const [copiedId, setCopiedId] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/item-requests`, getAuthHeaders());
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0
    }).format(price);
  };

  const getStatusInfo = (status) => statusConfig[status] || statusConfig.pending;

  const handleQuoteSubmit = async () => {
    if (!showQuoteDialog) return;
    
    setUpdating(showQuoteDialog);
    try {
      await axios.put(`${API}/item-requests/${showQuoteDialog}`, {
        purchase_cost: parseFloat(quoteForm.purchase_cost) || 0,
        service_fee: parseFloat(quoteForm.service_fee) || 0,
        delivery_fee: parseFloat(quoteForm.delivery_fee) || 0,
        admin_notes: quoteForm.admin_notes,
        status: 'quotation_sent'
      }, getAuthHeaders());
      
      toast.success('Quotation saved and status updated');
      setShowQuoteDialog(null);
      setQuoteForm({ purchase_cost: '', service_fee: '', delivery_fee: '', admin_notes: '' });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quotation');
    } finally {
      setUpdating(null);
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    setUpdating(requestId);
    try {
      await axios.put(`${API}/item-requests/${requestId}`, { status: newStatus }, getAuthHeaders());
      toast.success(`Status updated to ${statusConfig[newStatus].label}`);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const convertToOrder = async (requestId) => {
    setUpdating(requestId);
    try {
      const response = await axios.post(`${API}/item-requests/${requestId}/convert-to-order`, {}, getAuthHeaders());
      toast.success(`Converted to Order #${response.data.order_id}`);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to order');
    } finally {
      setUpdating(null);
    }
  };

  const openWhatsApp = async (requestId) => {
    try {
      const response = await axios.get(`${API}/item-requests/${requestId}/whatsapp-message`, getAuthHeaders());
      window.open(response.data.whatsapp_url, '_blank');
    } catch (error) {
      toast.error('Failed to generate WhatsApp message');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredRequests = requests.filter(req => {
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    const matchesSearch = 
      req.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.id?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    quotation_sent: requests.filter(r => r.status === 'quotation_sent').length,
    confirmed: requests.filter(r => r.status === 'confirmed').length,
    processing: requests.filter(r => r.status === 'processing').length,
    completed: requests.filter(r => r.status === 'completed').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0047AB]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-outfit font-bold text-slate-900">On-Demand Requests</h1>
        <Button onClick={fetchRequests} variant="outline" size="sm">
          <RefreshCcw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <p className="text-sm text-slate-500">Total</p>
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <p className="text-sm text-yellow-700">Pending</p>
          <p className="text-2xl font-bold text-yellow-800">{stats.pending}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <p className="text-sm text-blue-700">Quoted</p>
          <p className="text-2xl font-bold text-blue-800">{stats.quotation_sent}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <p className="text-sm text-green-700">Confirmed</p>
          <p className="text-2xl font-bold text-green-800">{stats.confirmed}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <p className="text-sm text-purple-700">Processing</p>
          <p className="text-2xl font-bold text-purple-800">{stats.processing}</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
          <p className="text-sm text-emerald-700">Completed</p>
          <p className="text-2xl font-bold text-emerald-800">{stats.completed}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, product, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending Review</option>
          <option value="quotation_sent">Quotation Sent</option>
          <option value="confirmed">Confirmed</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {filteredRequests.length === 0 ? (
          <div className="bg-white rounded-lg p-8 text-center border border-slate-200">
            <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No requests found</p>
          </div>
        ) : (
          filteredRequests.map((request) => {
            const statusInfo = getStatusInfo(request.status);
            const StatusIcon = statusInfo.icon;
            const isExpanded = expandedRequest === request.id;
            const nextStatuses = STATUS_TRANSITIONS[request.status] || [];

            return (
              <div
                key={request.id}
                className={`bg-white rounded-lg border ${statusInfo.border} overflow-hidden`}
              >
                {/* Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedRequest(isExpanded ? null : request.id)}
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${statusInfo.bg}`}>
                        <StatusIcon className={`w-5 h-5 ${statusInfo.color}`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{request.customer_name}</p>
                        <p className="text-sm text-slate-500">#{request.id} • {request.product_name}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {request.final_quote && (
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">{formatPrice(request.final_quote)}</p>
                          <p className="text-xs text-slate-500">Final Quote</p>
                        </div>
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-slate-100 p-4 bg-slate-50">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Customer & Product Info */}
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-slate-900 mb-2">Customer Details</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2 text-slate-600">
                              <Phone className="w-4 h-4" />
                              <a href={`https://wa.me/${request.whatsapp_number.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">
                                {request.whatsapp_number}
                              </a>
                            </div>
                            <div className="flex items-start gap-2 text-slate-600">
                              <MapPin className="w-4 h-4 mt-0.5" />
                              <span>{request.delivery_address}</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-600">
                              <Calendar className="w-4 h-4" />
                              {formatDate(request.created_at)}
                            </div>
                          </div>
                        </div>

                        <div>
                          <h4 className="font-medium text-slate-900 mb-2">Product Details</h4>
                          <div className="bg-white rounded-lg p-3 border border-slate-200">
                            <p className="font-medium text-slate-900">{request.product_name}</p>
                            {request.description && (
                              <p className="text-sm text-slate-600 mt-1">{request.description}</p>
                            )}
                            {request.category && (
                              <span className="inline-block mt-2 text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                                {request.category}
                              </span>
                            )}
                            {request.estimated_price && (
                              <p className="text-sm text-slate-500 mt-2">
                                Customer budget: {formatPrice(request.estimated_price)}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Images */}
                        {request.images && request.images.length > 0 && (
                          <div>
                            <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                              <Image className="w-4 h-4" />
                              Reference Images
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {request.images.map((img, idx) => (
                                <a key={`img-${request.id}-${idx}`} href={img} target="_blank" rel="noopener noreferrer" className="w-16 h-16 rounded-lg overflow-hidden border border-slate-200 hover:border-[#0047AB]">
                                  <img src={img} alt="" className="w-full h-full object-cover" />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Quotation & Actions */}
                      <div className="space-y-4">
                        {/* Current Quote */}
                        {(request.purchase_cost || request.service_fee || request.delivery_fee) && (
                          <div>
                            <h4 className="font-medium text-slate-900 mb-2">Price Breakdown</h4>
                            <div className="bg-white rounded-lg p-3 border border-slate-200 space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Purchase Cost</span>
                                <span className="font-medium">{formatPrice(request.purchase_cost || 0)}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Service Fee</span>
                                <span className="font-medium">{formatPrice(request.service_fee || 0)}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Delivery Fee</span>
                                <span className="font-medium">{formatPrice(request.delivery_fee || 0)}</span>
                              </div>
                              <div className="flex justify-between text-sm pt-2 border-t font-semibold">
                                <span>Total Quote</span>
                                <span className="text-[#0047AB]">{formatPrice(request.final_quote || 0)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {request.admin_notes && (
                          <div>
                            <h4 className="font-medium text-slate-900 mb-2">Admin Notes</h4>
                            <p className="text-sm text-slate-600 bg-white p-3 rounded-lg border border-slate-200">
                              {request.admin_notes}
                            </p>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="pt-4 border-t border-slate-200">
                          <h4 className="font-medium text-slate-900 mb-3">Actions</h4>
                          <div className="flex flex-wrap gap-2">
                            {/* Create/Update Quote */}
                            {(request.status === 'pending' || request.status === 'quotation_sent') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setQuoteForm({
                                    purchase_cost: request.purchase_cost || '',
                                    service_fee: request.service_fee || '',
                                    delivery_fee: request.delivery_fee || '',
                                    admin_notes: request.admin_notes || ''
                                  });
                                  setShowQuoteDialog(request.id);
                                }}
                                className="border-[#0047AB] text-[#0047AB]"
                              >
                                <IndianRupee className="w-4 h-4 mr-1" />
                                {request.final_quote ? 'Edit Quote' : 'Create Quote'}
                              </Button>
                            )}

                            {/* Send WhatsApp */}
                            {request.final_quote && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openWhatsApp(request.id)}
                                className="border-green-500 text-green-600 hover:bg-green-50"
                              >
                                <MessageCircle className="w-4 h-4 mr-1" />
                                Send WhatsApp
                              </Button>
                            )}

                            {/* Convert to Order */}
                            {request.status === 'confirmed' && (
                              <Button
                                size="sm"
                                onClick={() => convertToOrder(request.id)}
                                disabled={updating === request.id}
                                className="bg-[#0047AB] hover:bg-[#003380]"
                              >
                                <ArrowRight className="w-4 h-4 mr-1" />
                                Convert to Order
                              </Button>
                            )}

                            {/* Status Transitions */}
                            {nextStatuses.filter(s => s !== 'cancelled').map(status => (
                              <Button
                                key={status}
                                size="sm"
                                variant="outline"
                                onClick={() => updateStatus(request.id, status)}
                                disabled={updating === request.id}
                              >
                                Move to {statusConfig[status].label}
                              </Button>
                            ))}

                            {/* Cancel */}
                            {nextStatuses.includes('cancelled') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateStatus(request.id, 'cancelled')}
                                disabled={updating === request.id}
                                className="border-red-200 text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Cancel
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Quote Dialog */}
      <Dialog open={!!showQuoteDialog} onOpenChange={() => setShowQuoteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-[#0047AB]" />
              Create Quotation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Purchase Cost (₹)</Label>
              <Input
                type="number"
                value={quoteForm.purchase_cost}
                onChange={(e) => setQuoteForm(prev => ({ ...prev, purchase_cost: e.target.value }))}
                placeholder="Cost of the product in Dubai"
              />
            </div>
            <div>
              <Label>Service Fee (₹)</Label>
              <Input
                type="number"
                value={quoteForm.service_fee}
                onChange={(e) => setQuoteForm(prev => ({ ...prev, service_fee: e.target.value }))}
                placeholder="Your service charge"
              />
            </div>
            <div>
              <Label>Delivery Fee (₹)</Label>
              <Input
                type="number"
                value={quoteForm.delivery_fee}
                onChange={(e) => setQuoteForm(prev => ({ ...prev, delivery_fee: e.target.value }))}
                placeholder="Shipping & handling"
              />
            </div>
            <div className="bg-slate-50 p-3 rounded-lg">
              <div className="flex justify-between font-semibold">
                <span>Final Quote</span>
                <span className="text-[#0047AB]">
                  {formatPrice(
                    (parseFloat(quoteForm.purchase_cost) || 0) +
                    (parseFloat(quoteForm.service_fee) || 0) +
                    (parseFloat(quoteForm.delivery_fee) || 0)
                  )}
                </span>
              </div>
            </div>
            <div>
              <Label>Admin Notes (Optional)</Label>
              <Textarea
                value={quoteForm.admin_notes}
                onChange={(e) => setQuoteForm(prev => ({ ...prev, admin_notes: e.target.value }))}
                placeholder="Internal notes about this request"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuoteDialog(null)}>Cancel</Button>
            <Button onClick={handleQuoteSubmit} disabled={updating} className="bg-[#0047AB]">
              {updating ? 'Saving...' : 'Save & Send Quote'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRequestsPage;
